import { useEffect, useState } from 'react'
import { useAuthStore } from '@/stores/authStore'
import { PageFrame } from '@/components/layout/PageFrame'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card, CardContent, Skeleton } from '@/components/ui'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalBody,
  ModalFooter,
} from '@/components/ui'
import { useToast } from '@/components/ui'
import { PersonaEditor } from '@/components/config'
import { PersonaFilters } from '@/components/PersonaFilters'
import { PersonaCard } from '@/components/personas/PersonaCard'
import { BlankPersonaDialog } from '@/components/personas/BlankPersonaDialog'
import { useAppStore, usePersonaStore, defaultRagConfig } from '@/stores'
import { useSettingsStore } from '@/stores/settingsStore'
import { usePersonas, useFeature, usePersonaFilters, usePermission } from '@/hooks'
import type { PersonaEdit } from '@/hooks/usePersonas'
import type { Persona } from '@/stores'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Trash2,
  AlertTriangle,
  RefreshCw,
  Sparkles,
} from 'lucide-react'

export function PersonasPage() {
  const { activePersonaId, setActivePersonaId } = useAppStore()
  const { personas: personasMap, isLoading, error } = usePersonaStore()
  const showVoice = useFeature('voice')
  const showAvatars = useFeature('avatars')
  const showWhatsApp = useFeature('whatsapp')
  const showPersonaWizard = useFeature('persona_wizard')
  const showPersonaWorkshop = useFeature('persona_workshop')
  const navigate = useNavigate()
  const { createPersona, updatePersona, deletePersona, restorePersona, duplicatePersona, fetchPersonas, unlockPersona } = usePersonas()
  const { addToast } = useToast()

  const [searchQuery, setSearchQuery] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null)
  const [blankCreateOpen, setBlankCreateOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deletingPersona, setDeletingPersona] = useState<Persona | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [showArchived, setShowArchived] = useState(false)
  const hiddenPersonaIds = useSettingsStore((s) => s.ui.hiddenPersonaIds)
  const hidePersona = useSettingsStore((s) => s.hidePersona)
  const unhidePersona = useSettingsStore((s) => s.unhidePersona)
  const [showHidden, setShowHidden] = useState(false)
  // Backend gates `?hard=true` on personas:delete:hard (admin-only). UI uses
  // role as proxy; backend remains authoritative.
  const isAdmin = useAuthStore((s) => s.user?.roles?.includes('admin') ?? false)
  const currentUserId = useAuthStore((s) => s.user?.id)
  const canUpdatePersonas = usePermission('personas:update')
  const canCreatePersonas = usePermission('personas:create')
  // Admins (`personas:read:all`) can edit any unlocked persona they don't own,
  // mirroring the backend's admin edit-all path.
  const canReadAllPersonas = usePermission('personas:read:all')
  const [hardDeleteConfirm, setHardDeleteConfirm] = useState<Persona | null>(null)
  const [hardDeleteTyped, setHardDeleteTyped] = useState('')

  // Refetch when toggling archived view
  useEffect(() => {
    fetchPersonas({ includeArchived: showArchived })
  }, [showArchived, fetchPersonas])

  const personaList = Object.values(personasMap)
  const hiddenCount = personaList.filter((p) => hiddenPersonaIds[p.id]).length
  const browseList = showHidden
    ? personaList
    : personaList.filter((p) => !hiddenPersonaIds[p.id])

  const {
    filters,
    filtered: scopedPersonas,
    tagUniverse,
    activeCount,
    setScope,
    toggleTag,
    toggleType,
    clearAll,
  } = usePersonaFilters(browseList, { currentUserId, persistToUrl: true })

  const filteredPersonas = scopedPersonas.filter(
    (persona) =>
      persona.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (persona.description?.toLowerCase().includes(searchQuery.toLowerCase()) ?? false),
  )

  const handleCreateNew = () => {
    setBlankCreateOpen(true)
  }

  const handleBlankCreate = async (data: { name: string; description?: string }) => {
    try {
      const created = await createPersona({
        name: data.name,
        description: data.description,
        presentationMode: 'chat',
        ragConfig: defaultRagConfig,
      } as Omit<Persona, 'id' | 'createdAt' | 'updatedAt'>)
      addToast({
        title: 'Persona created',
        description: showPersonaWorkshop
          ? `"${created.name}" is ready — opening the Workshop.`
          : `"${created.name}" is ready.`,
        type: 'success',
      })
      navigate(`/personas/${created.id}/workshop`)
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to create persona',
        type: 'error',
      })
      throw err
    }
  }

  const handleEdit = (persona: Persona) => {
    if (showPersonaWorkshop) {
      navigate(`/personas/${persona.id}/workshop`)
    } else {
      setEditingPersona(persona)
      setEditorOpen(true)
    }
  }

  const handleOpenSettings = (persona: Persona) => {
    setEditingPersona(persona)
    setEditorOpen(true)
  }

  const handleToggleLock = async (persona: Persona) => {
    const next = !(persona.isLocked === true)
    try {
      await updatePersona(persona.id, { isLocked: next })
      addToast({
        title: next ? 'Persona locked' : 'Persona unlocked',
        description: next
          ? `"${persona.name}" is frozen — only you can edit it now.`
          : `"${persona.name}" can be edited again by admins and share recipients.`,
        type: 'success',
      })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to change lock state',
        type: 'error',
      })
    }
  }

  const handleAdminUnlock = async (persona: Persona) => {
    try {
      await unlockPersona(persona.id)
      addToast({
        title: 'Persona unlocked',
        description: `"${persona.name}" is no longer frozen — it can be edited again.`,
        type: 'success',
      })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to unlock persona',
        type: 'error',
      })
    }
  }

  const handleToggleHidden = (persona: Persona) => {
    if (hiddenPersonaIds[persona.id]) {
      unhidePersona(persona.id)
      addToast({
        title: 'Persona unhidden',
        description: `"${persona.name}" is back in your list.`,
        type: 'success',
      })
    } else {
      hidePersona(persona.id)
      addToast({
        title: 'Persona hidden',
        description: `"${persona.name}" is hidden from your list. Use "Show hidden" to bring it back.`,
        type: 'success',
      })
    }
  }

  const handleDuplicate = async (persona: Persona) => {
    try {
      const clone = await duplicatePersona(persona.id)
      addToast({
        title: 'Persona duplicated',
        description: `Created "${clone.name}".`,
        type: 'success',
      })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to duplicate persona',
        type: 'error',
      })
    }
  }

  const handleSavePersona = async (data: PersonaEdit) => {
    if (!editingPersona) return
    try {
      await updatePersona(editingPersona.id, data)
      addToast({
        title: 'Persona updated',
        description: `"${editingPersona.name}" has been updated successfully.`,
        type: 'success',
      })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to save persona',
        type: 'error',
      })
      throw err
    }
  }

  const handleDeleteClick = (persona: Persona) => {
    setDeletingPersona(persona)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingPersona) return

    setIsDeleting(true)
    try {
      // If this persona is the default, clear it first
      if (activePersonaId === deletingPersona.id) {
        setActivePersonaId(null)
      }

      await deletePersona(deletingPersona.id)
      addToast({
        title: 'Persona archived',
        description: `"${deletingPersona.name}" was archived. Use "Show archived" to restore it.`,
        type: 'success',
      })
      setDeleteConfirmOpen(false)
      setDeletingPersona(null)
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to delete persona',
        type: 'error',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRestore = async (persona: Persona) => {
    try {
      await restorePersona(persona.id)
      addToast({
        title: 'Persona restored',
        description: `"${persona.name}" is active again.`,
        type: 'success',
      })
      // Refetch in archived view to update the list
      await fetchPersonas({ includeArchived: showArchived })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to restore persona',
        type: 'error',
      })
    }
  }

  const handleConfirmHardDelete = async () => {
    if (!hardDeleteConfirm) return
    if (hardDeleteTyped !== hardDeleteConfirm.name) {
      addToast({
        title: 'Confirmation required',
        description: 'Type the persona name exactly to confirm.',
        type: 'error',
      })
      return
    }
    try {
      if (activePersonaId === hardDeleteConfirm.id) {
        setActivePersonaId(null)
      }
      await deletePersona(hardDeleteConfirm.id, { hard: true })
      addToast({
        title: 'Persona permanently deleted',
        description: `"${hardDeleteConfirm.name}" was removed permanently.`,
        type: 'success',
      })
      setHardDeleteConfirm(null)
      setHardDeleteTyped('')
      await fetchPersonas({ includeArchived: showArchived })
    } catch (err) {
      addToast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'Failed to permanently delete',
        type: 'error',
      })
    }
  }

  return (
    <PageFrame width="list" className="space-y-6">
      <PageHeader
        title="Expert Personas"
        subtitle="Create and manage AI expert configurations. Set a default persona for new conversations."
        actions={
          <>
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
              <input
                type="checkbox"
                checked={showArchived}
                onChange={(e) => setShowArchived(e.target.checked)}
                className="w-4 h-4 rounded-sm border-border text-primary focus:ring-ring"
              />
              Show archived
            </label>
            {(hiddenCount > 0 || showHidden) && (
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                  className="w-4 h-4 rounded-sm border-border text-primary focus:ring-ring"
                />
                Show hidden ({hiddenCount})
              </label>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchPersonas({ includeArchived: showArchived })}
              leftIcon={<RefreshCw className="w-4 h-4" />}
            >
              Refresh
            </Button>
            {showPersonaWizard ? (
              <>
                <Button
                  variant="secondary"
                  onClick={handleCreateNew}
                  leftIcon={<Plus className="w-4 h-4" />}
                >
                  Start blank
                </Button>
                <Button
                  onClick={() => navigate('/personas/new/wizard')}
                  leftIcon={<Sparkles className="w-4 h-4" />}
                >
                  New Persona
                </Button>
              </>
            ) : (
              <Button onClick={handleCreateNew} leftIcon={<Plus className="w-4 h-4" />}>
                New Persona
              </Button>
            )}
          </>
        }
      />

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search personas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Filters */}
      <PersonaFilters
        filters={filters}
        tagUniverse={tagUniverse}
        activeCount={activeCount}
        setScope={setScope}
        toggleTag={toggleTag}
        toggleType={toggleType}
        clearAll={clearAll}
        showVoice={showVoice}
        showAvatars={showAvatars}
      />

      {/* Error State */}
      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="py-4 flex items-center justify-between">
            <p className="text-destructive">{error}</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fetchPersonas()}
              leftIcon={<RefreshCw className="w-4 h-4" />}
            >
              Retry
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <Skeleton className="h-20 rounded-sm" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && personaList.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Plus className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">
              No personas yet
            </h3>
            <p className="text-muted-foreground text-center mb-4">
              Create your first expert persona to start customizing AI demonstrations.
            </p>
            <Button
              onClick={
                showPersonaWizard ? () => navigate('/personas/new/wizard') : handleCreateNew
              }
              leftIcon={<Plus className="w-4 h-4" />}
            >
              Create Persona
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Personas Grid */}
      {!isLoading && filteredPersonas.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {filteredPersonas.map((persona) => {
            const isDefault = activePersonaId === persona.id
            const isManaged = (persona.source ?? 'manual') !== 'manual'
            const isOwner = !!currentUserId && persona.userId === currentUserId
            const isLocked = persona.isLocked === true
            // Edit rights mirror the backend matrix (personas.py):
            //  - owner              → always
            //  - admin (read:all)   → any UNLOCKED, non-managed persona
            //  - shared recipient   → any UNLOCKED persona shared to them
            //  - locked             → owner only (a frozen canonical persona)
            //  - managed non-owned  → never (orchestrator-managed)
            const canEdit =
              canUpdatePersonas &&
              !(isManaged && !isOwner) &&
              (isOwner || (!isLocked && (canReadAllPersonas || persona.isShared === true)))
            const canArchive = isOwner // owner-only on the backend
            // Admin break-glass: a non-owner admin can lift a freeze (unlock),
            // which is separate from editing (a locked persona stays read-only
            // for them until they unlock). Mirrors POST /personas/{id}/unlock.
            const canAdminUnlock = !isOwner && !isManaged && isLocked && canReadAllPersonas

            return (
              <PersonaCard
                key={persona.id}
                persona={persona}
                isDefault={isDefault}
                canEdit={canEdit}
                canArchive={canArchive}
                canDuplicate={canCreatePersonas}
                isOwner={isOwner}
                canAdminUnlock={canAdminUnlock}
                isAdmin={isAdmin}
                showVoice={showVoice}
                showAvatars={showAvatars}
                showWhatsApp={showWhatsApp}
                activeTags={filters.tags}
                onToggleTag={toggleTag}
                onSetDefault={(p, makeDefault) => setActivePersonaId(makeDefault ? p.id : null)}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onArchive={handleDeleteClick}
                onRestore={handleRestore}
                onHardDelete={(p) => {
                  setHardDeleteConfirm(p)
                  setHardDeleteTyped('')
                }}
                onOpenSettings={handleOpenSettings}
                onToggleLock={handleToggleLock}
                onAdminUnlock={handleAdminUnlock}
                isHidden={!!hiddenPersonaIds[persona.id]}
                onToggleHidden={handleToggleHidden}
              />
            )
          })}
        </div>
      )}

      {/* No Results */}
      {!isLoading && personaList.length > 0 && filteredPersonas.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Search className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              No results found
            </h3>
            <p className="text-muted-foreground mb-4">
              No personas match your search or filters.
            </p>
            {!showHidden && hiddenCount > 0 && (
              <p className="text-muted-foreground mb-4">
                {hiddenCount} hidden — turn on "Show hidden" to see{' '}
                {hiddenCount === 1 ? 'it' : 'them'}.
              </p>
            )}
            {(activeCount > 0 || searchQuery) && (
              <Button
                variant="secondary"
                onClick={() => {
                  clearAll()
                  setSearchQuery('')
                }}
              >
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Persona Editor Modal */}
      {editingPersona && (
        <PersonaEditor
          open={editorOpen}
          onOpenChange={setEditorOpen}
          persona={editingPersona}
          onSave={handleSavePersona}
        />
      )}

      {/* Blank-Create Dialog (Q11) */}
      <BlankPersonaDialog
        open={blankCreateOpen}
        onOpenChange={setBlankCreateOpen}
        onCreate={handleBlankCreate}
      />

      {/* Delete Confirmation Modal */}
      <Modal open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Delete Persona
            </ModalTitle>
            <ModalDescription>
              This action cannot be undone.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <p className="text-muted-foreground">
              Archive{' '}
              <span className="font-medium text-foreground">
                "{deletingPersona?.name}"
              </span>
              ? It will be hidden from the default list but restorable from
              the "Show archived" view. Conversations are preserved.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              isLoading={isDeleting}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Archive
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Hard Delete Confirmation Modal (admin only, type-to-confirm) */}
      <Modal
        open={hardDeleteConfirm !== null}
        onOpenChange={(o) => {
          if (!o) {
            setHardDeleteConfirm(null)
            setHardDeleteTyped('')
          }
        }}
      >
        <ModalContent size="sm">
          <ModalHeader>
            <ModalTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              Permanently Delete Persona
            </ModalTitle>
            <ModalDescription>
              This action cannot be undone. The persona row is removed from
              the database. Conversations associated with this persona are
              preserved but lose their persona link.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            <p className="text-muted-foreground mb-3">
              To confirm, type the persona name exactly:{' '}
              <span className="font-mono font-medium text-foreground">
                {hardDeleteConfirm?.name}
              </span>
            </p>
            <Input
              value={hardDeleteTyped}
              onChange={(e) => setHardDeleteTyped(e.target.value)}
              placeholder="Type persona name to confirm"
              autoFocus
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setHardDeleteConfirm(null)
                setHardDeleteTyped('')
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmHardDelete}
              disabled={hardDeleteTyped !== hardDeleteConfirm?.name}
              leftIcon={<Trash2 className="w-4 h-4" />}
            >
              Permanently Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageFrame>
  )
}
